'use client'

import { ReactElement, useMemo, useState } from 'react'

import { Button } from '@syconium/little-miss-figgy'

import { gql } from '../../../../__generated__/graphql/catalog/gql'
import { CartContainer } from '../../../../brunswick/containers/cart'
import { useMakeCartItem } from '../../../../brunswick/containers/cart/utils'
import { useCartDialog, useCartInterstitialDialog } from '../../../_providers/CartProvider.client'
import { useTranslation } from '../../../_providers/TranslationProvider.client'
import { PricingRange, derivePricingForVariant } from '../../pricing/PricingRange.client'

import {
  ColorText,
  Details,
  DetailsActions,
  DetailsText,
  PriceText,
  ProductName,
  SizeSelector,
  SizeSelectorOption,
  Thumbnail,
  Tile,
} from './styles'

import type { CartInterstitialUpsellVariantFragment } from '../../../../__generated__/graphql/catalog/graphql'

gql(`
  fragment CartInterstitialUpsellVariant on ProductCatalogVariant {
    id
    urlPath
    shopifyId
    shopifyProductId
    sku
    availability
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
    group {
      id
      title
      siteWidePromoEligible
      finalSale
      gender
      primaryCategory {
        id
        key
      }
      images(first: 1) {
        url
      }
      color {
        id
        displayValue
      }
      variants {
        id
        urlPath
        shopifyId
        shopifyProductId
        sku
        availability
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
        }
        size {
          id
          displayValue
        }
        fit {
          id
          displayValue
        }
      }
      product {
        id
        handle
        options {
          sizes {
            size {
              id
              displayValue
            }
            outOfStock
          }
        }
      }
    }
  }
`)

export const CartInterstitialUpsellTile = ({
  variant,
}: {
  variant: CartInterstitialUpsellVariantFragment
}): ReactElement => {
  const variantGroup = variant.group
  const makeCartItem = useMakeCartItem()
  const { addItems } = CartContainer.useContainer()
  const { open: openCartDialog } = useCartDialog()
  const { close: closeCartInterstitial } = useCartInterstitialDialog()
  const { cart: miniCartCopy } = useTranslation()

  const [selectedSizeDisplayString, setSelectedSizeDisplayString] = useState('')

  const sizeOptions = useMemo(() => {
    const sizes = variantGroup.product.options.sizes
    if (sizes.length === 0) return [{ value: 'O/S', label: 'O/S', outOfStock: false }]
    return sizes.map(({ size, outOfStock }) => ({
      value: size.displayValue,
      label: outOfStock ? `${size.displayValue} ${miniCartCopy.soldOutLabel}` : size.displayValue,
      outOfStock,
    }))
  }, [miniCartCopy.soldOutLabel, variantGroup.product.options.sizes])

  const selectedSizeKey = useMemo(() => {
    const sizes = variantGroup.product.options.sizes
    return (
      sizes.find(size => size.size.displayValue === selectedSizeDisplayString)?.size.id ??
      variant.size?.id ??
      ''
    )
  }, [selectedSizeDisplayString, variant.size?.id, variantGroup.product.options.sizes])

  const selectedVariant = useMemo(() => {
    return (
      variantGroup.variants.find(
        v => v.size?.id === selectedSizeKey && v.fit?.id === variant.fit?.id
      ) ?? variant
    )
  }, [selectedSizeKey, variant, variantGroup.variants])

  const pricing = useMemo(() => derivePricingForVariant(selectedVariant), [selectedVariant])

  const imageUrl = variantGroup.images?.[0]?.url
  const colorDisplayValue = variantGroup.color?.displayValue

  return (
    <Tile>
      {imageUrl ? <Thumbnail $imgUrl={imageUrl} /> : null}
      <Details>
        <DetailsText>
          <ProductName>{variantGroup.title}</ProductName>
          {colorDisplayValue ? <ColorText>{colorDisplayValue}</ColorText> : null}
          {pricing ? (
            <PriceText>
              <PricingRange {...pricing} />
            </PriceText>
          ) : null}
        </DetailsText>
        <DetailsActions>
          {Boolean(sizeOptions.length) && (
            <SizeSelector
              value={selectedSizeDisplayString}
              onChange={setSelectedSizeDisplayString}
              heading={miniCartCopy.selectSize}
              isInvalid={false}
              placement='bottom-start'
              renderSelectValue={value => value || miniCartCopy.selectSize}
            >
              {sizeOptions.map(({ value, label, outOfStock }) => (
                <SizeSelectorOption key={value} value={value} disabled={outOfStock}>
                  {label}
                </SizeSelectorOption>
              ))}
            </SizeSelector>
          )}
          <Button
            allCaps
            block
            disabled={selectedVariant.availability !== 'AVAILABLE'}
            onClick={() => {
              addItems(
                makeCartItem({
                  product: variantGroup.product,
                  variant: selectedVariant,
                  variantGroup,
                  options: { attributedPortal: undefined },
                })
              )
              closeCartInterstitial()
              openCartDialog()
            }}
            size='small'
          >
            {miniCartCopy.add}
          </Button>
        </DetailsActions>
      </Details>
    </Tile>
  )
}
